import React, { useContext } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import { PrivacyModeContext } from "@/contexts";
import clsx from "clsx";
import {
  ArrowsRightLeftIcon,
  ReceiptPercentIcon,
} from "@heroicons/react/24/outline";
import { TransactionListContext } from "@/contexts";
import { DropdownMenu, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { SkeletonAvatar, SkeletonInput } from "@/components/ui/skeleton";
import {
  AuditTradeItemClass,
  useProfile,
  getTimezoneAbbr,
  getCurrencySymbol,
} from "@moonscape/shared";
import { TradeItemMenu } from "./TradeItemMenu";
import { CoinIcon } from "@/components";
import { Tag, TagTone } from "@/components/ms";
import { CELL, LedgerRow } from "./ledgerTable";
import { usePipelineState } from "@/hooks/usePipelineState";

/** Which tinted mono badge a transaction type earns. */
export const getTypeTag = (type: string): { tone: TagTone; label: string; isUnsupported: boolean } => {
  const lowerType = type?.toLowerCase();
  const label = type?.toUpperCase() || "";

  if (lowerType === "not found") {
    return { tone: "warn", label: "Unsupported", isUnsupported: true };
  }
  if (
    lowerType?.includes("buy") ||
    lowerType?.includes("deposit") ||
    lowerType?.includes("receive")
  ) {
    return { tone: "pos", label, isUnsupported: false };
  }
  if (
    lowerType?.includes("sell") ||
    lowerType?.includes("withdrawal") ||
    lowerType?.includes("send") ||
    lowerType === "gift_sent" ||
    lowerType === "donation"
  ) {
    return { tone: "neu", label, isUnsupported: false };
  }
  return { tone: "neu", label, isUnsupported: false };
};

export const TradeItemMedium = ({ t, isQueryLoading }) => {
  const { setModalOpen, setExtnTxnId } = useContext(TransactionListContext);
  const { privacyMode } = useContext(PrivacyModeContext);
  const { profile } = useProfile();
  const taxTimezone = profile?.taxTimezone ?? null;
  const { busy } = usePipelineState();

  const handleRowClick = () => {
    // A click still fires on mouseup after a text-selection drag - don't open
    // the modal out from under someone trying to copy a cell's text.
    if (window.getSelection()?.toString()) return;
    setExtnTxnId(t.ext_txn_id);
    setModalOpen(true);
  };

  const tradeItem = new AuditTradeItemClass(t);
  const displayData = tradeItem.getDisplayData();
  const typeTag = getTypeTag(t.type);

  // Use centralized methods from AuditTradeItemClass
  const coinSymbol = tradeItem.getCoinSymbol();
  const coinIconUrl = tradeItem.getCoinIconUrl();
  const coinGeckoId = tradeItem.getCoinGeckoId();
  const assetName = tradeItem.getDisplayAssetName();
  const primaryAmount = tradeItem.getDisplayAmount();
  const counterAmount = tradeItem.getDisplayCounterAmount();
  const date = tradeItem.formattedDate(taxTimezone);
  const symbol = getCurrencySymbol(
    tradeItem.final_valueasset || profile?.baseCurrency || "USD"
  );
  const feeQty = tradeItem.formattedFeeQty();

  // Loading state row
  if (isQueryLoading) {
    return (
      <tr className="block ms900:table-row">
        <td colSpan={8} className="block border-t border-ms-line py-3 ms900:table-cell">
          <div className="flex items-center gap-3">
            <SkeletonAvatar active size={30} />
            <div className="flex-1 space-y-2">
              <SkeletonInput active width={120} />
              <SkeletonInput active width={200} />
            </div>
            <SkeletonInput active width={80} />
          </div>
        </td>
      </tr>
    );
  }

  return (
    <LedgerRow
      key={displayData.transaction}
      className={clsx("cursor-pointer", typeTag.isUnsupported && "opacity-60")}
      onClick={handleRowClick}
    >
      {/* Coin mark */}
      <td className={CELL.asset}>
        <CoinIcon
          coinSymbol={coinSymbol}
          iconUrl={coinIconUrl}
          coingeckoId={coinGeckoId}
          className="h-[30px] w-[30px] flex-shrink-0 rounded-full"
        />
      </td>

      {/* What happened */}
      <td className={CELL.what}>
        <span className="block truncate text-ms-ink">{assetName}</span>
        <span className={CELL.sub}>
          {[displayData?.exchangeName, date].filter(Boolean).join(" · ")}
        </span>
        <span className="mt-1 flex items-center gap-2 ms900:mt-0.5">
          {tradeItem.deleted && <Tag tone="neg">Deleted</Tag>}
          {tradeItem.isTransferType() && (
            <ArrowsRightLeftIcon className="h-3.5 w-3.5 text-ms-dim" />
          )}
          {tradeItem.isInterestType() && (
            <ReceiptPercentIcon className="h-3.5 w-3.5 text-ms-dim" />
          )}
          {t.type?.toLowerCase() === "loss_writeoff" && (
            <span className="text-sm" title="Loss Write-off">
              💀
            </span>
          )}
          {tradeItem.txn_note && (
            <span
              className="max-w-[200px] truncate text-[10px] text-ms-dim"
              title={tradeItem.txn_note}
            >
              {tradeItem.txn_note}
            </span>
          )}
        </span>
      </td>

      {/* Date */}
      <td className={clsx(CELL.meta, privacyMode && "blur-md")}>
        {date}
        {taxTimezone && <span className="ml-1">{getTimezoneAbbr(taxTimezone)}</span>}
      </td>

      {/* Source */}
      <td className={CELL.meta}>{displayData?.exchangeName}</td>

      {/* Quantity */}
      <td className={clsx(CELL.num, "text-ms-ink", privacyMode && "blur-md")}>
        {primaryAmount}
        {counterAmount && (
          <span className="block text-[10.5px] text-ms-dim">{counterAmount}</span>
        )}
      </td>

      {/* Fee */}
      <td className={clsx(CELL.num, "text-ms-mute", privacyMode && "blur-md")}>
        {feeQty && feeQty !== "-" ? `${feeQty} ${tradeItem.feeasset ?? ""}` : "—"}
      </td>

      {/* Flag */}
      <td className={CELL.tag}>
        <Tag tone={typeTag.tone}>{typeTag.label}</Tag>
      </td>

      {/* Value — tan when we could not price it */}
      <td
        className={clsx(
          CELL.val,
          tradeItem.missing_final_value ? (busy ? "text-ms-mute" : "text-ms-tan") : "text-ms-ink",
          privacyMode && "blur-md"
        )}
      >
        {tradeItem.missing_final_value
          ? busy
            ? "Still calculating"
            : "Unpriced"
          : `${symbol}${tradeItem.formattedFiatValue()}`}
      </td>

      {/* Row menu */}
      <td
        className={clsx(CELL.asset, "text-right")}
        onClick={(e) => e.stopPropagation()}
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <span
              onClick={(e) => e.stopPropagation()}
              className="cursor-pointer px-1 text-ms-dim transition-colors hover:text-ms-ink"
            >
              <FontAwesomeIcon icon={faEllipsisV} />
            </span>
          </DropdownMenuTrigger>
          <TradeItemMenu t={t} view={handleRowClick} />
        </DropdownMenu>
      </td>
    </LedgerRow>
  );
};
